import React from 'react';
import { MessageCircle, Instagram, Mail, MapPin, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { socialLinks, trackAndOpenWhatsApp, whatsappURL } from '../data/mockData';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-fuchsia-900 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_fridge-plants-b2b/artifacts/ubica9bg_Design%20sem%20nome%20%2812%29.png"
                alt="Plantinha de Geladeira"
                className="h-20 w-auto"
              />
            </div>
            <p className="text-gray-300 leading-relaxed mb-6">
              Conectando pessoas com a natureza, fortalecendo relacionamentos e transformando ações em presença desde 2022.
            </p>
            <div className="flex gap-4">
              <Button
                onClick={trackAndOpenWhatsApp}
                className="bg-[#20BA5A] hover:bg-[#178C41] text-white"
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                WhatsApp
              </Button>
              <Button
                onClick={() => window.open(socialLinks.instagram, '_blank')}
                variant="outline"
                className="border-pink-500 text-pink-300 hover:bg-fuchsia-800"
              >
                <Instagram className="mr-2 h-4 w-4" />
                Instagram
              </Button>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-pink-300">Links Rápidos</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-300 hover:text-pink-300 transition-colors"
                >
                  Quem Somos
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('benefits')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-300 hover:text-pink-300 transition-colors"
                >
                  Benefícios
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('use-cases')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-300 hover:text-pink-300 transition-colors"
                >
                  Soluções B2B
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('media')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-300 hover:text-pink-300 transition-colors"
                >
                  Mídia
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-pink-300">Contato B2B</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-pink-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gray-300">WhatsApp Comercial</p>
                  <a 
                    href={whatsappURL}
                    onClick={(e) => {
                      e.preventDefault();
                      trackAndOpenWhatsApp();
                    }}
                    className="text-pink-300 hover:text-pink-200 cursor-pointer"
                  >
                    (11) 94165-1849
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-pink-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gray-300">Email</p>
                  <a 
                    href="mailto:contato@plantinhadegeladeira.com.br"
                    className="text-pink-300 hover:text-pink-200"
                  >
                    contato@plantinhadegeladeira.com.br
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-pink-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gray-300">Atendimento</p>
                  <p className="text-pink-300">Todo o Brasil</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-fuchsia-800/50">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <div className="text-center md:text-left">
              <p className="mb-1">
                © {currentYear} Plantinha de Geladeira. Todos os direitos reservados.
              </p>
              <p className="text-xs text-gray-500">
                X MAIS Y COMERCIO DE PLANTAS E FLORES LTDA - CNPJ: 46.022.517/0001-82
              </p>
            </div>
            <div className="flex gap-6">
              <a href="/politica-privacidade" className="hover:text-pink-300 transition-colors">
                Política de Privacidade
              </a>
              <a href="/termos-uso" className="hover:text-pink-300 transition-colors">
                Termos de Uso
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      <a
        href={whatsappURL}
        onClick={(e) => {
          e.preventDefault();
          trackAndOpenWhatsApp();
        }}
        className="fixed bottom-6 right-6 bg-[#25D366] hover:bg-[#20BA5A] text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-all duration-300 z-50 cursor-pointer"
        aria-label="Falar no WhatsApp"
        data-testid="floating-whatsapp"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413"/>
        </svg>
      </a>
    </footer>
  );
};
